import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { retryUnless } from '../lib/retryUnless'
import { formatCurrency, formatDate } from '../lib/utils'
import SowWorkplan from '../components/SowWorkplan'

// Client-facing only -- the public API never sends cost or markup (see the
// API's lib/publicSow.ts), and this page must never display them.
interface LineItem {
  id: string
  name: string
  description: string | null
  categoryName: string
  pricingType: string
  quantity: number
  estimatedHours: number | null
  unitPrice: number | null
  lineTotal: number
  isOptional: boolean
  tasks: { name: string; estimatedHours: number | null; phase?: string }[]
}

interface PublicPart {
  id: string
  name: string
  description: string | null
  identifier: string | null
  category: string | null
  manufacturer: string | null
  quantity: number
  unitPrice: number | null
  lineTotal: number
  isOptional: boolean
}

interface PublicSOW {
  title: string
  clientName: string
  mspName: string
  mspLogoUrl: string | null
  mspAddress: string
  mspPhone: string
  mspPrimaryColor: string
  preparedByName: string
  proposalDate: string
  executiveSummary: string
  lineItems: LineItem[]
  parts: PublicPart[]
  assumptions: string[]
  exclusions: string[]
  clientResponsibilities: string[]
  successCriteria: string[]
  appendixEnabled: boolean
  appendixText: string | null
  totals: {
    fixed: number
    timeAndMaterials: number
    hardware: number
    pmGovernanceHours: number
    pmGovernanceTotal: number
    partsTotal: number
    grand: number
  }
}

function formatHrs(h: number) {
  return h === 1 ? '1 hr' : `${h} hrs`
}

class TemporarilyUnavailableError extends Error {}

export default function SOWPublicView() {
  const { shareToken } = useParams<{ shareToken: string }>()
  const [searchParams] = useSearchParams()
  const autoPrint = searchParams.get('print') === '1'
  const [showAppendix, setShowAppendix] = useState(true)
  const queryClient = useQueryClient()

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['sow-public', shareToken],
    queryFn: async () => {
      // Token travels in a header, not the URL -- see the comment above
      // sowPublicView in apps/api/src/functions/sow.ts for why (platform
      // request telemetry captures the full URL, never header values).
      const res = await fetch('/api/v1/sow/public', { headers: { 'X-Share-Token': shareToken! } })
      if (res.status === 503) throw new TemporarilyUnavailableError('temporarily unavailable')
      if (!res.ok) throw new Error('SOW not found or link has expired')
      return res.json() as Promise<{ data: PublicSOW }>
    },
    enabled: !!shareToken,
    retry: retryUnless(queryClient, (err) => err instanceof TemporarilyUnavailableError),
  })

  const [pdfDownloading, setPdfDownloading] = useState(false)

  async function downloadPdf() {
    if (!shareToken || pdfDownloading) return
    setPdfDownloading(true)
    try {
      const res = await fetch('/api/v1/sow/public/pdf', { headers: { 'X-Share-Token': shareToken } })
      if (!res.ok) throw new Error('PDF not found or link has expired')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${data?.data.title ?? 'SOW'}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      window.alert('Could not download the PDF. The link may have expired.')
    } finally {
      setPdfDownloading(false)
    }
  }

  useEffect(() => {
    if (autoPrint && data) {
      const t = setTimeout(() => window.print(), 500)
      return () => clearTimeout(t)
    }
  }, [autoPrint, data])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    )
  }

  if (error instanceof TemporarilyUnavailableError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-900">This proposal is temporarily unavailable</p>
          <p className="mt-2 text-sm text-gray-500">Please try again shortly.</p>
          <button type="button" onClick={() => { void refetch() }} className="mt-4 rounded-md border px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100">Try again</button>
        </div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-900">SOW Not Found</p>
          <p className="mt-2 text-sm text-gray-500">
            This link may have expired or the document no longer exists.
          </p>
        </div>
      </div>
    )
  }

  const sow = data.data
  const primary = sow.mspPrimaryColor || '#0078d4'
  const required = sow.lineItems.filter(li => !li.isOptional)
  const optional = sow.lineItems.filter(li => li.isOptional)
  // Optional parts: listed under Optional Add-Ons, left out of the Parts
  // table and every total (the API already excludes them from totals).
  const requiredParts = sow.parts.filter(p => !p.isOptional)
  const optionalParts = sow.parts.filter(p => p.isOptional)
  const tmHours = required
    .filter(li => li.pricingType === 'time_and_materials')
    .reduce((sum, li) => sum + (li.estimatedHours ?? 0), 0)
  const totalLaborHours = tmHours + sow.totals.pmGovernanceHours
  const laborSubtotal = sow.totals.grand - sow.totals.partsTotal

  return (
    <div className="min-h-screen bg-gray-50 py-8 print:bg-white print:py-0">
      {/* Action buttons — hidden when printing */}
      <div className="mx-auto mb-4 flex max-w-4xl justify-end gap-2 px-4 print:hidden">
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print
        </button>
        <button
          onClick={downloadPdf}
          disabled={pdfDownloading}
          className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: primary }}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {pdfDownloading ? 'Downloading…' : 'Download PDF'}
        </button>
      </div>

      <div className="mx-auto max-w-4xl rounded-xl bg-white shadow-sm print:max-w-none print:rounded-none print:shadow-none">
        {/* Header — white bg, primary color bottom border */}
        <div
          className="rounded-t-xl border-b-4 p-10 print:rounded-none"
          style={{ borderBottomColor: primary }}
        >
          {/* Logo row */}
          <div className="mb-8 flex items-start justify-between">
            <div>
              {sow.mspLogoUrl ? (
                <img src={sow.mspLogoUrl} alt={sow.mspName} className="h-20 max-w-[220px] w-auto object-contain" />
              ) : (
                <p className="text-xl font-bold text-gray-900">{sow.mspName}</p>
              )}
            </div>
            <div className="text-right">
              <p className="font-semibold text-gray-900">{sow.mspName}</p>
              {sow.mspAddress && (
                <p className="mt-0.5 whitespace-pre-line text-sm text-gray-500">{sow.mspAddress}</p>
              )}
              {sow.mspPhone && (
                <p className="mt-0.5 text-sm text-gray-500">{sow.mspPhone}</p>
              )}
            </div>
          </div>

          {/* Title */}
          <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-gray-400">Proposal for</p>
          <h1 className="mb-8 text-3xl font-bold text-gray-900">{sow.title}</h1>

          {/* Meta grid */}
          <div className="grid grid-cols-3 gap-6 text-sm">
            <div>
              <p className="font-semibold text-gray-700">Prepared For</p>
              <p className="mt-0.5 text-gray-600">{sow.clientName}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700">Prepared By</p>
              <p className="mt-0.5 text-gray-600">{sow.preparedByName || sow.mspName}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700">Proposal Date</p>
              <p className="mt-0.5 text-gray-600">{formatDate(sow.proposalDate)}</p>
            </div>
          </div>
        </div>

        <div className="space-y-10 p-12">
          {/* Executive Summary */}
          {sow.executiveSummary && (
            <section>
              <h2 className="mb-3 text-xl font-bold text-gray-900">Executive Summary</h2>
              <div className="space-y-3 leading-relaxed text-gray-600">
                {sow.executiveSummary.split('\n').map(l => l.replace(/\r$/, '')).filter(Boolean).map((para, i) => (
                  <p key={i} className="whitespace-pre-wrap">{para}</p>
                ))}
              </div>
            </section>
          )}

          {/* Statement of Work — tasks grouped by phase */}
          <SowWorkplan items={sow.lineItems} />

          {/* Line Items - Labor */}
          <section>
            <h2 className="mb-4 text-xl font-bold text-gray-900">Line Items — Labor</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-semibold text-gray-700">Service</th>
                  <th className="pb-2 text-right font-semibold text-gray-700">Detail</th>
                  <th className="pb-2 text-right font-semibold text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {required.map(item => (
                  <tr key={item.id}>
                    <td className="py-3 pr-4">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      {item.description && <p className="mt-0.5 text-xs text-gray-500">{item.description}</p>}
                      <p className="mt-0.5 text-xs text-gray-400">{item.categoryName}</p>
                    </td>
                    <td className="py-3 text-right text-gray-500">
                      {item.pricingType === 'time_and_materials' && item.estimatedHours
                        ? formatHrs(item.estimatedHours)
                        : item.pricingType === 'hardware'
                          ? `${item.quantity} × ${formatCurrency(item.quantity > 0 ? item.lineTotal / item.quantity : item.lineTotal)}`
                          : '—'}
                    </td>
                    <td className="py-3 text-right font-medium text-gray-900">
                      {formatCurrency(item.lineTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {sow.totals.fixed > 0 && (
                  <tr>
                    <td colSpan={2} className="pt-3 text-sm text-gray-500">Fixed Price</td>
                    <td className="pt-3 text-right text-sm text-gray-700">{formatCurrency(sow.totals.fixed)}</td>
                  </tr>
                )}
                {sow.totals.timeAndMaterials > 0 && (
                  <tr>
                    <td colSpan={2} className="text-sm text-gray-500">
                      Time &amp; Materials{tmHours > 0 ? ` (${formatHrs(tmHours)})` : ''}
                    </td>
                    <td className="text-right text-sm text-gray-700">{formatCurrency(sow.totals.timeAndMaterials)}</td>
                  </tr>
                )}
                {sow.totals.hardware > 0 && (
                  <tr>
                    <td colSpan={2} className="text-sm text-gray-500">Hardware</td>
                    <td className="text-right text-sm text-gray-700">{formatCurrency(sow.totals.hardware)}</td>
                  </tr>
                )}
                {sow.totals.pmGovernanceTotal > 0 && (
                  <tr>
                    <td colSpan={2} className="text-sm text-gray-500">
                      PM Governance &amp; Oversight{sow.totals.pmGovernanceHours > 0 ? ` (${formatHrs(sow.totals.pmGovernanceHours)})` : ''}
                    </td>
                    <td className="text-right text-sm text-gray-700">{formatCurrency(sow.totals.pmGovernanceTotal)}</td>
                  </tr>
                )}
                <tr className="border-t-2 border-gray-900">
                  <td colSpan={2} className="pt-3 text-base font-bold text-gray-900">
                    Labor Subtotal{totalLaborHours > 0 ? ` (${formatHrs(totalLaborHours)})` : ''}
                  </td>
                  <td className="pt-3 text-right text-lg font-bold text-gray-900">
                    {formatCurrency(laborSubtotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>

          {/* Line Items - Parts */}
          {requiredParts.length > 0 && (
            <section>
              <h2 className="mb-4 text-xl font-bold text-gray-900">Line Items — Parts</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-semibold text-gray-700">Part</th>
                    <th className="pb-2 text-right font-semibold text-gray-700">Qty</th>
                    <th className="pb-2 text-right font-semibold text-gray-700">Unit Price</th>
                    <th className="pb-2 text-right font-semibold text-gray-700">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {requiredParts.map(part => (
                    <tr key={part.id}>
                      <td className="py-3 pr-4">
                        <p className="font-medium text-gray-900">{part.name}</p>
                        {part.description && <p className="mt-0.5 text-xs text-gray-500">{part.description}</p>}
                        <p className="mt-0.5 text-xs text-gray-400">
                          {[part.identifier ? `#${part.identifier}` : null, part.category, part.manufacturer].filter(Boolean).join(' · ')}
                        </p>
                      </td>
                      <td className="py-3 text-right text-gray-500">{part.quantity}</td>
                      <td className="py-3 text-right text-gray-500">
                        {part.unitPrice != null ? formatCurrency(part.unitPrice) : '—'}
                      </td>
                      <td className="py-3 text-right font-medium text-gray-900">
                        {formatCurrency(part.lineTotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t">
                    <td colSpan={3} className="pt-3 text-sm font-semibold text-gray-900">Parts Subtotal</td>
                    <td className="pt-3 text-right text-sm font-semibold text-gray-900">
                      {formatCurrency(sow.totals.partsTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </section>
          )}

          {/* Total Investment */}
          <section>
            <h2 className="mb-4 text-xl font-bold text-gray-900">Total Investment</h2>
            <table className="w-full text-sm">
              <tbody>
                <tr>
                  <td className="py-2 text-gray-700">
                    Labor Subtotal{totalLaborHours > 0 ? ` (${formatHrs(totalLaborHours)})` : ''}
                  </td>
                  <td className="py-2 text-right text-gray-700">{formatCurrency(laborSubtotal)}</td>
                </tr>
                {sow.totals.partsTotal > 0 && (
                  <tr>
                    <td className="py-2 text-gray-700">Parts Subtotal</td>
                    <td className="py-2 text-right text-gray-700">{formatCurrency(sow.totals.partsTotal)}</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-900">
                  <td className="pt-3 text-base font-bold text-gray-900">Total Investment</td>
                  <td className="pt-3 text-right text-lg font-bold text-gray-900">
                    {formatCurrency(sow.totals.grand)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>

          {/* Optional Add-Ons */}
          {(optional.length > 0 || optionalParts.length > 0) && (
            <section>
              <h2 className="mb-1 text-xl font-bold text-gray-900">Optional Add-Ons</h2>
              <p className="mb-4 text-sm text-gray-500">The following items are not included in the total above but are available upon request.</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-semibold text-gray-700">Service</th>
                    <th className="pb-2 text-right font-semibold text-gray-700">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {optional.map(item => (
                    <tr key={item.id}>
                      <td className="py-3 pr-4">
                        <p className="font-medium text-gray-900">{item.name}</p>
                        {item.description && <p className="mt-0.5 text-xs text-gray-500">{item.description}</p>}
                        <p className="mt-0.5 text-xs text-gray-400">{item.categoryName}</p>
                      </td>
                      <td className="py-3 text-right font-medium text-gray-700">{formatCurrency(item.lineTotal)}</td>
                    </tr>
                  ))}
                  {optionalParts.map(part => (
                    <tr key={part.id}>
                      <td className="py-3 pr-4">
                        <p className="font-medium text-gray-900">{part.name}</p>
                        {part.description && <p className="mt-0.5 text-xs text-gray-500">{part.description}</p>}
                        <p className="mt-0.5 text-xs text-gray-400">
                          {[
                            part.identifier ? `#${part.identifier}` : null,
                            part.category,
                            part.manufacturer,
                            part.unitPrice != null ? `${part.quantity} × ${formatCurrency(part.unitPrice)}` : `Qty ${part.quantity}`,
                          ].filter(Boolean).join(' · ')}
                        </p>
                      </td>
                      <td className="py-3 text-right font-medium text-gray-700">{formatCurrency(part.lineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Appendix toggle */}
          {(sow.assumptions.length > 0 || sow.exclusions.length > 0 || sow.successCriteria.length > 0 || (sow.appendixEnabled && !!sow.appendixText)) && (
            <div className="flex items-center justify-between border-t pt-6 print:hidden">
              <p className="text-sm font-semibold text-gray-700">Appendix</p>
              <button
                onClick={() => setShowAppendix(v => !v)}
                className="flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
              >
                {showAppendix ? 'Hide' : 'Show'} Appendix
              </button>
            </div>
          )}

          {showAppendix && (
            <>
              {/* Assumptions */}
              {sow.assumptions.length > 0 && (
                <section>
                  <h2 className="mb-3 text-xl font-bold text-gray-900">Assumptions</h2>
                  <ul className="space-y-1.5">
                    {sow.assumptions.map((a, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Exclusions */}
              {sow.exclusions.length > 0 && (
                <section>
                  <h2 className="mb-3 text-xl font-bold text-gray-900">Exclusions &amp; Out-of-Scope</h2>
                  <ul className="space-y-1.5">
                    {sow.exclusions.map((e, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                        {e}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Client Responsibilities */}
              {sow.clientResponsibilities?.length > 0 && (
                <section>
                  <h2 className="mb-3 text-xl font-bold text-gray-900">Client Responsibilities</h2>
                  <ul className="space-y-1.5">
                    {sow.clientResponsibilities.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Success Criteria */}
              {sow.successCriteria.length > 0 && (
                <section>
                  <h2 className="mb-3 text-xl font-bold text-gray-900">Success Criteria</h2>
                  <ul className="space-y-1.5">
                    {sow.successCriteria.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* MSA / Terms & Conditions */}
              {sow.appendixEnabled && sow.appendixText && (
                <section className="border-t pt-6">
                  <h2 className="mb-3 text-xl font-bold text-gray-900">Terms &amp; Conditions</h2>
                  <div className="space-y-2 text-sm leading-relaxed text-gray-600">
                    {sow.appendixText.split('\n').map((line, i) =>
                      line.trim() ? <p key={i}>{line}</p> : <br key={i} />
                    )}
                  </div>
                </section>
              )}
            </>
          )}

          {/* Footer */}
          <div className="border-t pt-8 text-center text-xs text-gray-400">
            <p>Prepared by {sow.mspName} · {formatDate(sow.proposalDate)}</p>
            <p className="mt-1">This document is confidential and intended solely for {sow.clientName}.</p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 0.75in; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  )
}
